import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

import type { DashboardRecord } from "@/lib/dashboard/types";
import { buildDashboardCopyInput, useCreateDashboard } from "./useCreateDashboard";

/** Where a freshly duplicated dashboard is opened: its editor, ready to be adjusted. */
export function dashboardEditPath(id: string): string {
  return `/workspace/dashboards/${encodeURIComponent(id)}/edit`;
}

/**
 * Duplicates a dashboard as a draft copy and opens the copy in the editor.
 * Shared by every screen that offers "Duplicate dashboard", so they cannot drift.
 */
export function useDuplicateDashboard() {
  const navigate = useNavigate();
  const { mutateAsync, isPending, isError } = useCreateDashboard();

  const duplicate = useCallback(
    async (dashboard: DashboardRecord) => {
      if (isPending) return;
      try {
        const copy = await mutateAsync(buildDashboardCopyInput(dashboard));
        navigate(dashboardEditPath(copy.id));
      } catch {
        // `isError` drives the message shown by the screen.
      }
    },
    [isPending, mutateAsync, navigate],
  );

  return { duplicate, isDuplicating: isPending, isDuplicateError: isError };
}
